import React, { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { App_url } from "../utils/constants/static";
import { SplitText } from "gsap/SplitText";

gsap.registerPlugin(ScrollTrigger, SplitText);

export default function Navbar() {
  const logoContainerRef = useRef<HTMLDivElement | null>(null);
  const titleContainerRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const navRef = useRef<HTMLDivElement | null>(null)
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetch("/logo.svg")
      .then((res) => res.text())
      .then((svgText) => {
        if (!logoContainerRef.current) return;
        // if (logoContainerRef.current) {
        // Fix only the <image> href to use absolute URL
        svgText = svgText.replace(
          /href="(\/logo-inner-1\.png)"/,
          `href="${window.location.origin}/logo-inner-1.png"`
        );
        logoContainerRef.current.innerHTML = svgText;

        // Animate the inner rect (your logo image)
        const innerRect =
          logoContainerRef.current.querySelector(".zhan-inner");
        if (innerRect) {
          gsap.set(innerRect, { x: 50, opacity: 50 });
          gsap.to(innerRect, {
            x: 0,
            opacity: 50,
            duration: 1.5,
            ease: "power2.inOut",
          });
        }
        // }
      });

    // Load Title SVG inline
    fetch("/title.svg")
      .then((res) => res.text())
      .then((svgText) => {
        if (titleContainerRef.current) {
          titleContainerRef.current.innerHTML = svgText;

          // Fix hidden letters: some have clip-path="0"
          // const hiddenChars =
          //   titleContainerRef.current.querySelectorAll(".svg-char.leave");
          // hiddenChars.forEach((g) => {
          //   g.style.clipPath = "polygon(0 0, 0% 0, 0% 100%, 0 100%)";
          // });

          setLoaded(true);
        }
      });
  }, []);

  useEffect(() => {
    if (!loaded) return;

    const letters = titleContainerRef?.current?.querySelectorAll(".svg-char");
    if (!letters) return;

    // Animate OCCUPY letters
    const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

    tl.fromTo(
      titleContainerRef.current,
      { y: 30, scale: 1.5, opacity: 0, },
      {
        y: -30,
        xPercent: 26,
        scale: 0.9,
        opacity: 1,
        ease: "power2.out",
        duration: 1.2,
      },
    );

    // Fade in ling.webp image AFTER text animation finishes
    tl.fromTo(
      imgRef.current,
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 1, ease: "power2.out" },
      "-=0.8", // overlap slightly with last letters
    );
  }, [loaded]);

  // ScrollTrigger for navbar to fixed at the top
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    ScrollTrigger.create({
      trigger: nav,
      start: 'top top',
      end: 'bottom bottom',
      endTrigger: "body",
      markers: false,
      onEnter: () =>
        gsap.to(nav, {
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%'
        }),
      onLeaveBack: () =>
        gsap.to(nav, {
          position: 'relative',
          width: '100%'
        }),
    })

    return () => ScrollTrigger.getAll().forEach((t) => t.kill());
  }, []);

  // navbar links animation
  const handleMouseEnter = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const el = e.currentTarget;
    const [main, clone] = el.querySelectorAll(".link");

    const mainChars = main.querySelectorAll("span");
    const cloneChars = clone.querySelectorAll("span");

    gsap.to(mainChars, {
      y: -20,
      opacity: 0.6,
      duration: 0.4,
      ease: "power2.out",
      stagger: 0.03,
    });

    gsap.fromTo(cloneChars, {
      y: 10, opacity: 0
    }, {
      y: 0,
      opacity: 1,
      duration: 0.6,
      ease: "power3.out",
      stagger: 0.03,
    })
  }

  const handleMouseLeave = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const el = e.currentTarget;
    const [main, clone] = el.querySelectorAll(".link");
    if (!main || !clone) return;

    const mainChars = main.querySelectorAll("span");
    const cloneChars = clone.querySelectorAll("span");

    gsap.to(cloneChars, {
      y: 20,
      opacity: 0,
      duration: 0.3,
      ease: "power2.in",
      stagger: {
        each: 0.03,
        from: "end"
      },
    });

    gsap.fromTo(mainChars, {
      y: -20, opacity: 0,
    }, {
      y: 0,
      opacity: 1,
      duration: 0.6,
      ease: "power2.out",
      stagger: {
        each: 0.03,
        from: "end"
      },
    });

  }


  return (
    <>
      <div className="relative w-full min-h-[80vh] bg-black text-white overflow-hidden">
        <video
          ref={videoRef}
          src="/kv2.mp4"
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover opacity-80"
        />
        {/* Logo */}
        <div className="relative z-10 flex justify-between gap-6 md:gap-8 lg:gap-10 max-w-full">
          <div ref={logoContainerRef} className="flex p-[24px] shrink-0"></div>
          <div ref={titleContainerRef}
            className="flex relative min-w-[40vw] md:min-w-[45vw] lg:min-w-[55vw] overflow-hidden"></div>
          <img
            ref={imgRef}
            src={App_url.image.ling}
            alt="Ling"
            className="h-[180px] md:h-[220px] p-[24px] lg:h-[260px] object-cover opacity-90 pointer-events-auto "
          />
        </div>
        {/* <div className=""> */}
        {/* Navbar Links */}
        <nav ref={navRef} className="nav-link relative z-40 flex justify-between px-10 text-white text-sm uppercase">
          {["About", "Collection", "Experience", "Contact"].map((item) => (
            <a
              key={item}
              href={`#${item.toLowerCase()}`}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              className="cursor-pointer"
            >
              <div className="flex flex-col relative overflow-hidden">
                <span className="link relative flex z-10">
                  {item.split('').map((char, i) => (
                    <span key={i} className="inline-block">{char}</span>
                  ))}
                </span>
                <span className="link absolute top-0 left-0 flex">
                  {item.split("").map((char, i) => (
                    <span key={i} className="inline-block">{char}</span>
                  ))}
                </span>
              </div>
            </a>
          ))}
          {/* <a href="#about" className="hover:text-gray-300 transition">About</a>
          <a href="#collection" className="hover:text-gray-300 transition">Collection</a>
          <a href="#experience" className="hover:text-gray-300 transition">Experience</a>
          <a href="#contact" className="hover:text-gray-300 transition">Contact</a> */}
        </nav>
        {/* </div> */}

      </div>
    </>
  );
}




